import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Axios from "axios";
import { NODE_HOST_URL_API3_v1 } from "../api/Api";
import styles from "./WhatsappWidget.module.css";
import { customTableStylesForSummery } from "../../Utils/customTableStyles";
import Spining from "../../UI/Spining/Spining";

const WhatsappWidgetStep4 = () => {
  const lStorage = JSON.parse(localStorage.getItem("login"));
  const userId = lStorage?.user?.userId;
  const userToken = lStorage?.token;

  const [widgetData, setWidgetData] = useState([]);
  const [noDataMessage, setNoDataMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("active");
  const [dateFilter, setDateFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const isActive = (status) => status === true || status === 1;

  const fetchWidgets = async (page, limit, filter = statusFilter, df = dateFilter, fd = fromDate, td = toDate) => {
    setLoading(true);
    try {
      const body = {
        user_id: userId,
        token: userToken,
        method: "retrieve_wp_widget",
        page,
      };
      if (limit) body.limit = limit;
      if (filter) body.status_filter = filter;
      if (df) {
        body.date_filter = df;
        if (df === "custom" && fd && td) {
          body.from_date = fd;
          body.to_date = td;
        }
      }
      const res = await Axios.post(`${NODE_HOST_URL_API3_v1}/whatsapp_widget`, body);

      const rows = (res.data.data || []).map((widget) => ({
        id: widget.id,
        domains: widget.installed_domains?.map((d) => d.domain) || [],
        installed_at: widget.installed_domains?.[0]?.installed_at || "-",
        active: isActive(widget.status),
      }));

      setWidgetData(rows);
      setTotalRows(res.data.pagination?.total || 0);
      if (res.data.pagination?.limit) setPerPage(res.data.pagination.limit);
      if (!res.data.success) setNoDataMessage(res.data.message || "No data found.");
    } catch (error) {
      console.error("Error fetching widgets:", error);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchWidgets(page, perPage, statusFilter, dateFilter, fromDate, toDate);
  };

  const handleStatusFilter = (filter) => {
    setStatusFilter(filter);
    setCurrentPage(1);
    fetchWidgets(1, perPage || 10, filter, dateFilter, fromDate, toDate);
  };

  const handleDateFilterChange = (e) => {
    const val = e.target.value;
    setDateFilter(val);
    setCurrentPage(1);
    if (val !== "custom") {
      setFromDate("");
      setToDate("");
      fetchWidgets(1, perPage || 10, statusFilter, val, "", "");
    }
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchWidgets(1);
  }, []);

  useEffect(() => {
    if (dateFilter === "custom" && fromDate && toDate) {
      fetchWidgets(1, perPage || 10, statusFilter, dateFilter, fromDate, toDate);
      setCurrentPage(1);
    }
  }, [fromDate, toDate]);

  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      width: "100px",
    },
    {
      name: "Domain",
      cell: (row) =>
        row.domains.length > 0 ? (
          <div className={styles.domainCell}>
            {row.domains.map((domain, i) => (
              <span key={i}>
                <a href={domain.startsWith("http") ? domain : `https://${domain}`} target="_blank" rel="noopener noreferrer">
                  {domain}
                </a>
                {i < row.domains.length - 1 && ", "}
              </span>
            ))}
          </div>
        ) : "-",
    },
    {
      name: "Installed At",
      selector: (row) => row.installed_at,
    },
    {
      name: "Status",
      width: "130px",
      cell: (row) =>
        row.active ? (
          <span className={styles.statusActive}>Active</span>
        ) : (
          <span className={styles.statusInactive}>Inactive</span>
        ),
    },
  ];

  return (
    <main className="content">
      <div className="container-fluid p-0">
        <div className="row">
          <div className="col-auto d-none d-sm-block">
            <h3 className="page-title" style={{ textAlign: "left" }}>
              Step 4 – Check the widget status after embedding the widget code into your website.
            </h3>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-12 col-lg-12 col-xxl-12">
            <div className={styles.dateFilterRow}>
              <span className={styles.dateFilterLabel}>Date Filter:</span>
              <select
                value={dateFilter}
                onChange={handleDateFilterChange}
                className={styles.dateFilterSelect}
              >
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="custom">Custom Range</option>
              </select>
              {dateFilter === "custom" && (
                <>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    className={styles.dateInput}
                    max={toDate || undefined}
                  />
                  <span className={styles.dateRangeSep}>to</span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={handleToDateChange}
                    className={styles.dateInput}
                    min={fromDate || undefined}
                  />
                </>
              )}
            </div>
            <div className="card flex-fill table-content-show">
              <div className={styles.filterBarHeader}>
                <span className={styles.filterBarTitle}>Widget List</span>
                <div className={styles.filterPillsWrap}>
                  {["active", "inactive"].map((f) => {
                    const isSelected = statusFilter === f;
                    const pillClass = isSelected
                      ? (f === "active" ? styles.filterPillGreenActive : styles.filterPillRedActive)
                      : "";
                    const dotClass = isSelected
                      ? (f === "active" ? styles.filterDotGreen : styles.filterDotRed)
                      : styles.filterDotGrey;
                    return (
                      <button
                        key={f}
                        onClick={() => handleStatusFilter(isSelected ? "" : f)}
                        className={`${styles.filterPill} ${pillClass}`}
                      >
                        <span className={`${styles.filterDot} ${dotClass}`} />
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="table-responsive">
                {initialLoad ? (
                  <Spining />
                ) : (
                  <DataTable
                    columns={columns}
                    data={widgetData}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    paginationComponentOptions={{ noRowsPerPage: true }}
                    onChangePage={handlePageChange}
                    progressPending={loading}
                    progressComponent={<Spining />}
                    highlightOnHover
                    customStyles={customTableStylesForSummery}
                    noHeader
                    noDataComponent={
                      <div className="p-4 text-center text-muted">
                        {noDataMessage || "No widgets found."}
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default WhatsappWidgetStep4;
